import React from 'react';
import styled from 'styled-components';
import { set, get, isEmpty } from 'lodash';

import { csvExportLabels as messages } from './messages';
import { parseMonthIntoDate } from './utils';

import { FormattedMessage, useIntl } from 'react-intl';
import getSymbolFromCurrency from 'currency-symbol-map';

import { CSVLink } from 'react-csv';

const FooterContainer = styled.div`
    padding: 5px;
    padding-top: 15px;
    text-align: left;

    a {
        font-size: 12px;
        color: inherit;
    }
`;

const Footer = ({ data, currency }) => {
    const intl = useIntl();
    const filename = `${intl.formatMessage(messages.exportedFileName)}.csv`;
    const formatRooms = (value) => intl.formatNumber(value);
    const formatRevenue = (value) => {
        const currencySymbol = getSymbolFromCurrency(currency);
        const currencyValue = intl.formatNumber(value / 100, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        return `${currencySymbol}${currencyValue}`;
    };
    const formatBookings = (value) => {
        if (isEmpty(value)) {
            return 0;
        }
        return intl.formatNumber(value.length);
    };
    const fieldFormatters = [
        { field: 'gain.guaranteed.rooms', formatter: formatRooms },
        { field: 'gain.nonGuaranteed.rooms', formatter: formatRooms },
        { field: 'loss.rooms', formatter: formatRooms },
        {
            field: 'gain.guaranteed.revenue.accommodation',
            formatter: formatRevenue,
        },
        {
            field: 'gain.nonGuaranteed.revenue.accommodation',
            formatter: formatRevenue,
        },
        { field: 'loss.revenue.accommodation', formatter: formatRevenue },
        {
            field: 'gain.guaranteed.bookings.NEW_BOOKING',
            formatter: formatBookings,
        },
        {
            field: 'gain.guaranteed.bookings.DATE_CHANGE',
            formatter: formatBookings,
        },
        {
            field: 'gain.guaranteed.bookings.ROOM_CHANGE',
            formatter: formatBookings,
        },
        {
            field: 'gain.nonGuaranteed.bookings.NEW_BOOKING',
            formatter: formatBookings,
        },
        {
            field: 'gain.nonGuaranteed.bookings.DATE_CHANGE',
            formatter: formatBookings,
        },
        {
            field: 'gain.nonGuaranteed.bookings.ROOM_CHANGE',
            formatter: formatBookings,
        },
        { field: 'loss.bookings.CANCELLED', formatter: formatBookings },
        { field: 'loss.bookings.DATE_CHANGE', formatter: formatBookings },
        { field: 'loss.bookings.ROOM_CHANGE', formatter: formatBookings },
    ];
    const exportData = data.map((row) => ({
        stayDate: intl.formatDate(parseMonthIntoDate(row.stayDate), {
            year: 'numeric',
            month: 'long',
        }),
        ...fieldFormatters.reduce(
            (formattedRoomsObject, { field, formatter }) =>
                set({ ...formattedRoomsObject }, field, formatter(get(row, field))),
            {},
        ),
    }));

    const headers = [
        {
            label: intl.formatMessage(messages.monthColumn),
            key: 'stayDate',
        },
        {
            label: intl.formatMessage(messages.guaranteedRoomsGainColumn),
            key: 'gain.guaranteed.rooms',
        },
        {
            label: intl.formatMessage(messages.nonGuaranteedRoomsGainColumn),
            key: 'gain.nonGuaranteed.rooms',
        },
        {
            label: intl.formatMessage(messages.roomsLossColumn),
            key: 'loss.rooms',
        },
        {
            label: intl.formatMessage(messages.guaranteedRevenueGainColumn),
            key: 'gain.guaranteed.revenue.accommodation',
        },
        {
            label: intl.formatMessage(messages.nonGuaranteedRevenueGainColumn),
            key: 'gain.nonGuaranteed.revenue.accommodation',
        },
        {
            label: intl.formatMessage(messages.revenueLossColumn),
            key: 'loss.revenue.accommodation',
        },
        {
            label: intl.formatMessage(messages.bookingsNewBookingColumn, {
                type: 'Guaranteed',
            }),
            key: 'gain.guaranteed.bookings.NEW_BOOKING',
        },
        {
            label: intl.formatMessage(messages.bookingsDateChangeColumn, {
                type: 'Guaranteed',
            }),
            key: 'gain.guaranteed.bookings.DATE_CHANGE',
        },
        {
            label: intl.formatMessage(messages.bookingsRoomChangeColumn, {
                type: 'Guaranteed',
            }),
            key: 'gain.guaranteed.bookings.ROOM_CHANGE',
        },
        {
            label: intl.formatMessage(messages.bookingsNewBookingColumn, {
                type: 'Unguaranteed',
            }),
            key: 'gain.nonGuaranteed.bookings.NEW_BOOKING',
        },
        {
            label: intl.formatMessage(messages.bookingsDateChangeColumn, {
                type: 'Unguaranteed',
            }),
            key: 'gain.nonGuaranteed.bookings.DATE_CHANGE',
        },
        {
            label: intl.formatMessage(messages.bookingsRoomChangeColumn, {
                type: 'Unguaranteed',
            }),
            key: 'gain.nonGuaranteed.bookings.ROOM_CHANGE',
        },
        {
            label: intl.formatMessage(messages.bookingsCancelledColumn, {
                type: 'Loss',
            }),
            key: 'loss.bookings.CANCELLED',
        },
        {
            label: intl.formatMessage(messages.bookingsDateChangeColumn, {
                type: 'Loss',
            }),
            key: 'loss.bookings.DATE_CHANGE',
        },
        {
            label: intl.formatMessage(messages.bookingsRoomChangeColumn, {
                type: 'Loss',
            }),
            key: 'loss.bookings.ROOM_CHANGE',
        },
    ];

    return (
        <FooterContainer>
            <CSVLink headers={headers} data={exportData} filename={filename}>
                <FormattedMessage {...messages.exportLink} />
            </CSVLink>
        </FooterContainer>
    );
};

export default Footer;
